/*
 * Keeps the header transparent for as long as the hero is the thing behind it.
 *
 * Two traps here, both found by measuring rather than reading:
 *
 * 1. Dawn's `scrolled-past-header` fires once you scroll past the HEADER --
 *    about 60px -- not past the hero. Keying the overlay off it turned the bar
 *    opaque white after the slightest scroll while the film was still directly
 *    behind it. Measured: clear at y=0, opaque by y=200.
 *
 * 2. The homepage hero is STICKY. It holds its place while the sections below
 *    ride up over it, so its bounding rect stays 0..viewport at every scroll
 *    position. An IntersectionObserver on the hero therefore reports
 *    "intersecting" forever and the header never goes opaque at all. That was
 *    the first version of this file and it was wrong.
 *
 * So the thing to watch is not the hero but whatever covers it: once the next
 * section's top edge rises past the header, the bar is over ordinary page
 * content and has to take its background back.
 *
 * The CSS this drives is additive. If this script never runs, the
 * `scrolled-past-header` behaviour stands and the header is never left as
 * white type on a white bar.
 */
(() => {
  const hero = document.querySelector('.pa-hero--under-header, .cinema-opening');

  // theme.liquid decides `pa-hero-under-header` from the template, which is
  // where it has to be decided: it needs to be in the markup before the header
  // group renders, or the header would be laid out in flow and then yanked out
  // of it. That list can drift from the templates, so reconcile it against the
  // element that is actually on the page. A correction here costs one reflow;
  // being wrong costs an opaque bar over the hero, or white type on white.
  document.body.classList.toggle('pa-hero-under-header', !!hero);
  if (!hero) return;

  const BEHIND = 'pa-hero-over-header';
  const section = hero.matches('.cinema-opening') ? hero : (hero.closest('.shopify-section') || hero);
  const cover = section.nextElementSibling;

  // Dawn keeps --header-height current. Fall back rather than trusting a NaN
  // into the comparison, which would make it silently always-false.
  const headerHeight = () => {
    const n = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height'));
    return Number.isFinite(n) && n > 0 ? n : 64;
  };

  const update = () => {
    // With no following section, fall back to the hero's own bottom edge, which
    // is correct for a non-sticky hero and harmless for a sticky one.
    const edge = cover ? cover.getBoundingClientRect().top : hero.getBoundingClientRect().bottom;
    document.body.classList.toggle(BEHIND, edge > headerHeight());
  };

  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      update();
    });
  };

  update();
  addEventListener('scroll', schedule, { passive: true });
  addEventListener('resize', schedule, { passive: true });

  // The hero is full-viewport and the film loads late, so the section below it
  // can move after first paint. Re-check when the box actually changes rather
  // than polling.
  if ('ResizeObserver' in window) {
    const ro = new ResizeObserver(schedule);
    ro.observe(section);
    if (cover) ro.observe(cover);
  }
})();
